/**
 * useImageHandling
 *
 * Centralised image URL resolution for the Technomac frontend.
 *
 * The Laravel API (MachineImageResource) returns three fields per image:
 *   - file_path  : raw DB path e.g. "machines/xxx.jpg"
 *   - url        : full asset URL e.g. "http://127.0.0.1:8000/storage/machines/xxx.jpg"
 *   - full_url   : same as url
 *
 * In development Nuxt proxies /storage/** → http://127.0.0.1:8000/storage/**
 * so we always want to return a relative /storage/... path and let the proxy
 * handle it.  In production NUXT_PUBLIC_API_BASE is set to the real backend
 * origin, so we prefix with that instead.
 */
export const useImageHandling = () => {
  // Call useRuntimeConfig once per composable instance, not per image render.
  const config = useRuntimeConfig();

  /**
   * Derive the backend origin from apiBase.
   * apiBase is either:
   *   dev  : '/api/v1'          → backendUrl = ''
   *   prod : 'https://api.x.com/api/v1' → backendUrl = 'https://api.x.com'
   */
  const backendUrl = (() => {
    const base = config.public.apiBase || '';
    try {
      const url = new URL(base);
      return url.origin; // 'https://api.x.com'
    } catch {
      // base is a relative path like '/api/v1' — strip the path segment
      return base.replace(/\/api\/v\d+.*$/, ''); // '' in dev
    }
  })();

  const isProduction = backendUrl.startsWith('http');

  /**
   * Resolve any image path/URL to the correct URL for the current environment.
   *
   * @param {string|null}  imagePath    Raw path from API
   * @param {string}       fallbackPath Shown when path is empty or unrecognised
   * @returns {string}
   */
  const getSafeImageUrl = (imagePath, fallbackPath = '/images/placeholder-machine.svg') => {
    if (!imagePath || typeof imagePath !== 'string') return fallbackPath;

    // ── 1. Full absolute URL (http/https) ──────────────────────────────────
    // Always extract the /storage/... portion so Nuxt's proxy handles it in
    // dev, or prefix with the correct production origin.
    if (imagePath.startsWith('http')) {
      try {
        const parsed = new URL(imagePath);
        if (parsed.pathname.includes('/storage/')) {
          const storagePath = parsed.pathname.substring(
            parsed.pathname.indexOf('/storage')
          );
          return isProduction ? `${backendUrl}${storagePath}` : storagePath;
        }
        // Non-storage absolute URLs (e.g. external CDN) — return as-is
        return imagePath;
      } catch {
        // Fall through if URL is somehow malformed
      }
    }

    // ── 2. Already starts with /storage ────────────────────────────────────
    if (imagePath.startsWith('/storage')) {
      return isProduction ? `${backendUrl}${imagePath}` : imagePath;
    }

    // ── 3. Starts with storage/ (no leading slash) ─────────────────────────
    if (imagePath.startsWith('storage/')) {
      return isProduction ? `${backendUrl}/${imagePath}` : `/${imagePath}`;
    }

    // ── 4. Relative path from storage root ─────────────────────────────────
    //    e.g. "machines/xxx.jpg", "spare-parts/xxx.jpg", "carousel/xxx.jpg"
    if (
      imagePath.startsWith('machines/') ||
      imagePath.startsWith('spare-parts/') ||
      imagePath.startsWith('SparePart/') ||
      imagePath.startsWith('carousel/') ||
      imagePath.startsWith('services/') ||
      imagePath.startsWith('settings/')
    ) {
      return isProduction
        ? `${backendUrl}/storage/${imagePath}`
        : `/storage/${imagePath}`;
    }

    // ── 5. Unrecognised format ─────────────────────────────────────────────
    return fallbackPath;
  };

  /**
   * Recover gracefully when an <img> fails to load.
   * The guard prevents an infinite error loop if the placeholder itself 404s.
   *
   * @param {Event}  event
   * @param {string} fallbackPath
   */
  const handleImageError = (event, fallbackPath = '/images/placeholder-machine.svg') => {
    const img = event.target;
    if (img && img.src !== window.location.origin + fallbackPath) {
      img.src = fallbackPath;
      img.alt = 'Image not available';
    }
  };

  /**
   * Return a resolved { file_path, alt } object for the nth image of a machine.
   * Tries file_path → url → full_url in order so we always get a usable URL.
   *
   * @param {Object} machine
   * @param {number} index   0-based image index
   * @returns {{ file_path: string, alt: string }}
   */
  const getMachineImage = (machine, index = 0) => {
    const placeholder = {
      file_path: '/images/placeholder-machine.svg',
      alt: `${machine?.title || 'Machine'} — Image not available`,
    };

    if (!machine?.images?.length) return placeholder;

    const firstImageMedia = machine.images.find((item) => {
      const mimeType = item?.mime_type || '';
      return !(typeof mimeType === 'string' && mimeType.startsWith('video/'));
    });

    const image = machine.images[index] ?? firstImageMedia ?? machine.images[0];
    if (!image) return placeholder;

    // Prefer file_path; fall back to url / full_url
    const rawPath = image.file_path || image.url || image.full_url;
    const resolvedUrl = getSafeImageUrl(rawPath);

    return {
      file_path: resolvedUrl,
      // Support both 'alt' and 'alt_text' field names from the API
      alt: image.alt_text || image.alt || `${machine.title} — Image ${index + 1}`,
    };
  };

  /**
   * Lightweight existence check via HTTP HEAD request.
   * Use sparingly — prefer handleImageError for runtime fallbacks.
   *
   * @param {string} imagePath
   * @returns {Promise<boolean>}
   */
  const checkImageExists = async (imagePath) => {
    try {
      const response = await fetch(imagePath, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  return {
    getSafeImageUrl,
    handleImageError,
    getMachineImage,
    checkImageExists,
  };
};
